import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Clock, Loader2, Lock, Play, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import {
  useDisruptionProgress,
  useResolutionState,
  useTriggerAutoRebook,
} from '@/lib/disruptions/use-disruptions';
import { BulkInProgressError, type DisruptionActiveRun } from '@/lib/disruptions/types';
import { operatorLabel, vendorListLabel } from './labels';
import { RebookingProgressDialog } from './rebooking-progress-dialog';
import { RebookingPolicyNotice } from './rebooking-policy-notice';
import { rebookingPolicyGate } from './resolution-steps';

// Disruption-wide rebooking trigger (S6 / NEX-281, S16 / NEX-292). "Generate all
// options" acts across the WHOLE event in ONE BFF call — the domain owns the
// per-case/per-group fan-out. Hotel allocation moved to its own Hotels tab
// (EventAllocationActionBar) so each op has a single home; this bar is now
// rebooking-only.
//
// S16 (NEX-292) — the bulk ops are disruption-scoped-locked in the domain: only
// one run per disruption at a time, ACROSS BOTH ops. We DISABLE while a run is
// active (derived from the progress poll's `activeRun` — which may be an
// allocation run started from the Hotels tab — and optimistically from the last
// 409) and, on a 409, name the holder. The lock auto-releases when the run reaches
// a terminal state — the poll drops `activeRun` and we re-enable.
const BULK_BAR_ENABLED = true;

interface RosterBulkBarProps {
  disruptionUrn: string;
  /**
   * `bar` (default) — the original horizontal action bar. `sidebar` (U2) — a
   * vertical, full-width stack for the roster action sidebar that ALSO exposes a
   * separate "Generate options" trigger (generate-only, `acceptTopRank:false`)
   * above the accept-top-rank action. Both variants share ONE progress dialog +
   * one disruption-scoped lock. The default preserves the frozen bar behaviour.
   */
  variant?: 'bar' | 'sidebar';
}

export function RosterBulkBar({ disruptionUrn, variant = 'bar' }: RosterBulkBarProps) {
  // Pre-run policy gate (rebooking TDS §18) — read HERE, once, because this bar is
  // the rebooking trigger on every classic surface: the Overview stepper, the
  // Passengers action sidebar and Command Center V2. When the station's policy has
  // the engine off, a run could only end POLICY_DISABLED for every group, so the
  // trigger is withheld and the rule is named instead (Tower hides its own
  // "Rebook again" on the same signal). Degrades open: an unreadable aggregate
  // leaves the bar exactly as it was.
  const { data: resolution } = useResolutionState(disruptionUrn);
  const gate = rebookingPolicyGate(resolution?.steps);

  if (!BULK_BAR_ENABLED) return null;
  if (gate.disabled) {
    return (
      <RebookingPolicyNotice
        policyUrn={gate.policyUrn}
        data-testid="roster-bulk-policy-gate"
      />
    );
  }

  return <BulkBar disruptionUrn={disruptionUrn} variant={variant} />;
}

function BulkBar({ disruptionUrn, variant = 'bar' }: RosterBulkBarProps) {
  const { t } = useTranslation('roster');
  const sidebar = variant === 'sidebar';
  // Opening the live progress dialog the moment a run starts — the trigger is
  // here in Passengers but the run feedback is the modal (no Progress tab).
  const [progressOpen, setProgressOpen] = useState(false);
  const autoRebook = useTriggerAutoRebook(disruptionUrn);
  // The progress poll is the source of truth for the live run — it carries
  // `activeRun` while a run is in flight and drops it when the run is terminal.
  const progress = useDisruptionProgress(disruptionUrn);
  const polledRun: DisruptionActiveRun | null = progress.data?.activeRun ?? null;
  // Options were already produced for this disruption → the action is a RE-run.
  const hasOptions = (progress.data?.optionsGenerated ?? 0) > 0;
  // Live vendor delay (E): a provider rate-limiting OR responding slowly during the
  // in-flight run. Shown as a calm "delayed/slow by Z" hint — the run is progressing,
  // just slower, NOT failing.
  const delayedBy = polledRun ? (progress.data?.delayedBy ?? []) : [];
  const slowBy = polledRun ? (progress.data?.slowBy ?? []) : [];

  // Last run started from this bar — shown inline so the operator can correlate
  // the runId with the live progress.
  const [lastRunId, setLastRunId] = useState<string | null>(null);
  // The lock holder surfaced by a 409 (optimistic, until the poll catches up).
  const [conflict, setConflict] = useState<{ operatorUrn: string } | null>(null);

  // The poll is authoritative: once it reports no active run, clear any stale 409
  // banner so the bar re-enables the moment the run goes terminal (or on TTL).
  useEffect(() => {
    if (!polledRun) setConflict(null);
  }, [polledRun]);

  // "Active" = a run the poll sees in flight (either op), OR a 409 we just caught.
  const holderUrn = polledRun?.operatorUrn ?? conflict?.operatorUrn ?? null;
  const locked = holderUrn !== null;
  const disabled = autoRebook.isPending || locked;
  // A run EXISTS for this disruption (RUNNING / QUEUED / COMPLETED / FAILED — anything
  // but NONE) → the progress dialog has something to show. Drives the persistent
  // "View progress" affordance so the operator can reopen it after a reload or once
  // the run completed (activeRun + the session lastRunId are both gone by then).
  const hasRun = !!progress.data && progress.data.runStatus !== 'NONE';

  const onTrigger = (acceptTopRank: boolean, regenerate = false): void => {
    // Open the dialog OPTIMISTICALLY — the moment the operator clicks, before the
    // trigger resolves. The trigger's prelude (resolve + plan the disruption's
    // groups) can take a few seconds on a large event; opening here means a slow
    // (or timed-out) trigger response never leaves the operator without the live
    // dialog — progress arrives over the WS regardless of the HTTP round-trip.
    setProgressOpen(true);
    autoRebook.mutate(
      // `regenerate` forces a fresh generation run when options already exist (the
      // domain preserves ticketed / selected groups and only recomputes open ones).
      { acceptTopRank, ...(regenerate ? { regenerate: true } : {}) },
      {
        onSuccess: (res) => {
          setLastRunId(res.runId);
          setConflict(null);
        },
        onError: (err) => {
          // The trigger failed/aborted — revert the optimistic open. If a run did
          // start (e.g. a slow trigger the UI gave up on), the progress poll still
          // sees it and the persistent "View progress" button reopens the dialog.
          setProgressOpen(false);
          if (err instanceof BulkInProgressError) setConflict({ operatorUrn: err.inProgressBy });
        },
      },
    );
  };

  // A non-409 failure (503 etc.) — the inline "couldn't start" hint. A 409 is NOT
  // an error from the operator's POV; it's surfaced via the lock banner + toast.
  const hardError = !!autoRebook.error && !(autoRebook.error instanceof BulkInProgressError);

  return (
    <>
      <div
        className={cn(
          'rounded-lg border border-border bg-muted/40 text-sm',
          sidebar
            ? 'flex flex-col gap-2 p-3'
            : 'flex shrink-0 flex-wrap items-center gap-3 px-3 py-2',
        )}
        data-testid="roster-bulk-bar"
      >
        {sidebar ? (
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('bulk.title')}
          </h3>
        ) : (
          <span className="text-muted-foreground">{t('bulk.barHint')}</span>
        )}

        {/* Status / lock / error feed — calm + inline (never blocks the operator, §18). */}
        {locked ? (
          <span
            className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400"
            role="status"
            data-testid="bulk-bar-locked"
          >
            <Lock className="h-3 w-3" aria-hidden />
            {t('bulk.locked', { operator: operatorLabel(holderUrn!) })}
          </span>
        ) : hardError ? (
          <span className="text-xs text-destructive" data-testid="bulk-bar-error">
            {t('bulk.startError')}
          </span>
        ) : lastRunId ? (
          <span className="text-xs text-muted-foreground" data-testid="bulk-bar-run">
            {t('bulk.running')}{' '}
            <span className="font-mono" title={lastRunId}>
              {shortRunId(lastRunId)}
            </span>
          </span>
        ) : null}

        {/* Live vendor-delay hint (E) — the run is progressing, just slowed by a
            rate-limited OR slow provider. Calm + inline; explicitly not a failure. */}
        {delayedBy.length > 0 || slowBy.length > 0 ? (
          <span
            className="inline-flex items-center gap-1 text-xs text-sky-600 dark:text-sky-400"
            role="status"
            data-testid="bulk-bar-delayed"
          >
            <Clock className="h-3 w-3" aria-hidden />
            {delayedBy.length > 0
              ? t('bulk.delayed', { vendors: vendorListLabel(delayedBy) })
              : t('bulk.slow', { vendors: vendorListLabel(slowBy) })}
          </span>
        ) : null}

        {/* 409 toast — fires once when a click loses the race; names the holder (§18). */}
        {conflict ? (
          <span className="sr-only" role="alert" data-testid="bulk-bar-toast">
            {t('bulk.alreadyRunning', { operator: operatorLabel(conflict.operatorUrn) })}
          </span>
        ) : null}

        <div className={cn(sidebar ? 'flex flex-col gap-2' : 'ml-auto flex gap-2')}>
          {/* Re-open the live progress dialog any time a run exists — in flight, this
              session, OR completed (survives a reload). Fixes "watch the live
              progress" with no way to watch. */}
          {polledRun || lastRunId || hasRun ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setProgressOpen(true)}
              className={cn(sidebar && 'w-full justify-center')}
              data-testid="bulk-view-progress"
            >
              <Activity className="mr-1 h-3 w-3" aria-hidden />
              {/* While a run is in flight → "View progress"; once it reached a terminal
                  state the dialog shows the outcome, so → "View result". */}
              {polledRun || autoRebook.isPending ? t('bulk.viewProgress') : t('bulk.viewResult')}
            </Button>
          ) : null}

          {/* Sidebar (U2): a SEPARATE generate-only trigger (acceptTopRank:false) so
              the operator can run the engine without auto-accepting — the MVP's
              "Generate options for all". The accept action stays the bar's
              existing button below. */}
          {sidebar ? (
            <Button
              variant="default"
              size="sm"
              onClick={() => onTrigger(false, hasOptions)}
              disabled={disabled}
              className="w-full justify-center"
              data-testid="bulk-generate-options"
            >
              {autoRebook.isPending ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
              ) : (
                <Play className="mr-1 h-3 w-3" aria-hidden />
              )}
              {hasOptions ? t('bulk.regenerateNoBooking') : t('bulk.generateNoBooking')}
            </Button>
          ) : null}

          {/* The accept-top-rank shortcut is the FROZEN `bar` variant's only trigger.
              In the sidebar (U2) roster, acceptance lives entirely in the configurable
              AUTOMATIC ACCEPTANCE panel below (rank / cap / dry-run) — so we do NOT
              render a redundant one-click "accept best" here (option B). */}
          {!sidebar ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onTrigger(true)}
              disabled={disabled}
              data-testid="bulk-auto-rebook"
            >
              {autoRebook.isPending ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
              ) : (
                <Sparkles className="mr-1 h-3 w-3" aria-hidden />
              )}
              {hasOptions ? t('bulk.regenerateRebooking') : t('bulk.generateRebooking')}
            </Button>
          ) : null}
        </div>
      </div>

      <RebookingProgressDialog
        disruptionUrn={disruptionUrn}
        open={progressOpen}
        onOpenChange={setProgressOpen}
        op="rebooking"
        onRerun={() => onTrigger(true)}
      />
    </>
  );
}

/** runId is a secondary identifier — show a short head, full value on hover (§18). */
function shortRunId(runId: string): string {
  return runId.length > 12 ? `${runId.slice(0, 12)}…` : runId;
}
